'use client'

import { ProductInfoRating } from '@/app/[locale]/product/[slug]/_components/product-info/product-info-rating'
import { ExpandableText } from '@/components/ui/expandable-text'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { DateUtil } from '@/lib/utils/date'
import { ReviewItem } from '@/types/collection'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'

const ProductReviewItemGallery = dynamic(
  () =>
    import(
      '@/app/[locale]/product/[slug]/_components/product-reviews/product-review-item-gallery'
    ).then((mod) => mod.ProductReviewItemGallery),
  {
    loading: () => null,
  }
)

type Props = {
  review: ReviewItem
}
type GalleryImage = {
  largeURL: string
  thumbnailURL: string
  width: number
  height: number
}

// Đường dẫn ảnh logo các nguồn
const IMAGE_SOURCE = {
  SHOPEE: '/icons/third-party/shopee.svg',
  // LAZADA: '/icons/third-party/lazada.svg', // TODO: chưa có ảnh
  TIKTOK: '/icons/third-party/tiktok.svg',
}

export const ProductReviewsItem = ({ review }: Props) => {
  const t = useTranslations('product_detail.title')

  let reviewImages: GalleryImage[] = []
  if (review?.images?.length) {
    reviewImages = review.images.map((image) => ({
      largeURL: ImageHelper.getImageUrl(image),
      thumbnailURL: ImageHelper.getImageUrl(image),
      width: 1200,
      height: 1600,
    }))
  }

  return (
    <article aria-label='Product review item' role='listitem'>
      <div className='relative rounded-xl bg-light p-6'>
        <div className='flex items-center'>
          <p className='line-clamp-1 font-sans text-base font-medium leading-5 text-neutral-800 max-md:w-2/3'>
            {review.fullname}
          </p>
          <span className='mx-3 inline-block h-1 w-1 rounded-full bg-neutral-300'></span>
          <time className='font-sans text-sm leading-4.5 text-neutral-400'>
            {DateUtil.formatShort(review.created_at)}
          </time>
        </div>
        <ProductInfoRating
          rating={{ avg: review.score, count: 0 }}
          showText={false}
          showAvgText={false}
          className='my-2 [&_img]:h-4.5 [&_img]:w-4.5'
        />

        {/* TODO: chưa có data */}
        {/* <ul className='flex flex-wrap items-center gap-x-8 gap-y-1 lg:w-1/2'>
          {[
            {
              title: 'Kích thước',
              value: 'XXLXXLXXLXX',
            },
            {
              title: 'Màu sắc',
              value: 'Đen',
            },
            {
              title: 'Phù hợp với cơ thể',
              value: 'Đúng kích thước',
            },
            {
              title: 'Độ dày',
              value: '10mm',
            },
            {
              title: 'Độ rộng',
              value: '10mm',
            },
          ].map((item) => (
            <li className='flex items-start gap-1' key={item.title}>
              <p className='font-sans text-sm font-bold text-neutral-400'>
                {item.title}:
              </p>
              <p className='font-sans text-sm text-neutral-400'>{item.value}</p>
            </li>
          ))}
        </ul> */}
        <ExpandableText
          className='mt-2 text-neutral-700 lg:mt-4 lg:text-base lg:leading-5.5'
          text={review.message}
          maxLines={3}
        />
        {reviewImages.length > 0 && (
          <ProductReviewItemGallery images={reviewImages} />
        )}

        {/* TODO: chưa có data */}
        {/* <ul className='mt-4 flex items-start gap-1 lg:hidden'>
          <li>
            <Tag title='Sản phẩm đẹp' className='h-8 rounded-full' />
          </li>
          <li>
            <Tag title='Giá tốt' className='h-8 rounded-full' />
          </li>
        </ul> */}

        <div className='mt-4 flex items-center justify-between lg:mt-6'>
          {/* TODO: chưa có data */}
          {/* <ul className='flex items-start gap-1 max-lg:hidden'>
            <li>
              <Tag title='Sản phẩm đẹp' className='h-8 rounded-full' />
            </li>
            <li>
              <Tag title='Giá tốt' className='h-8 rounded-full' />
            </li>
          </ul>
          <div className='flex items-center gap-1'>
            <p className='font-sans text-xs text-neutral-400'>
              Đánh giá này có <br className='lg:hidden' /> hữu ích không?
            </p>
            <div className='group flex cursor-pointer gap-1'>
              <ThumbsUp
                className='text-neutral-500 group-hover:text-primary'
                width={22}
                height={22}
              />
              <p className='font-sans text-base text-neutral-400 group-hover:text-primary'>
                {' '}
                20
              </p>
            </div>
          </div> */}

          <div></div>

          {review.source && review.source in IMAGE_SOURCE && (
            <div className='lg:absolute lg:right-6 lg:top-6 lg:flex lg:items-center lg:gap-2'>
              <p className='mb-1 font-sans text-xs italic text-neutral-900/50'>
                {t('review_from')}
              </p>
              <Image
                src={IMAGE_SOURCE[review.source as keyof typeof IMAGE_SOURCE]}
                width={60}
                height={18}
                alt='source icon'
                draggable={false}
              />
            </div>
          )}
        </div>
      </div>
      {review.feedback && <ReviewItemReply feedback={review.feedback} />}
    </article>
  )
}

const ReviewItemReply = ({ feedback }: { feedback: string }) => {
  const t = useTranslations('product_detail.title')

  return (
    <div className='relative mt-4 rounded-xl bg-neutral-200 px-6 py-4'>
      <div className='absolute -top-3 left-10 h-0 w-0 rounded-sm border-x-[12px] border-b-[12px] border-x-transparent border-b-neutral-200' />

      <div className='mb-2 flex items-center'>
        <Image
          src={'/images/logo-circle.svg'}
          alt='logo coolmate '
          width={28}
          height={28}
          draggable={false}
        />
        <p className='ml-1 font-sans text-base font-medium leading-5.5 text-neutral-800'>
          {t('reply_from_coolmate')}
        </p>
        <span className='mx-1 inline-block h-1 w-1 rounded-full bg-neutral-300 lg:mx-3'></span>
      </div>
      <ExpandableText
        text={feedback}
        maxLines={3}
        expandText={t('read_more')}
        className='lg:text-base lg:leading-5.5'
      />
    </div>
  )
}
